/* 루미나 '홈 문구' — 관리자 사이트설정 > 디자인설정 > 루미나 > "2. 디자인 토큰" > "홈 문구"(design_tokens.home_copy)를 읽는 한 곳.
   WHY(2026-09-30 운영자 지시): 승인 시안의 구간 설명 글·"지금 시작하기" 안내 문장·스포츠 화면 설명을 처음 구현 때 "예시 문구"로 보고
        빼면서 관리 칸도 만들지 않아, 메인에 글자가 없고 관리자에서 넣을 방법도 없었다. 글자는 관리자에서 고치고,
        홈·스포츠·경기결과 화면은 이 모듈만 거쳐 읽는다(화면마다 따로 파싱하지 않는다).
   규칙: 제목 칸이 비면 화면의 기본 제목(부르는 쪽이 넘기는 값)을 그린다 — 제목 없는 빈 구간 머리가 생기지 않게.
        설명·문장 칸이 비면 그 줄을 그리지 않는다. 앞뒤 공백은 여기서 자른다(관리자 입력은 띄어쓰기를 칠 수 있게 자르지 않고 저장한다).
   파싱은 다른 디자인 토큰과 같은 규칙(문자열이면 JSON 으로 풀고, 실패하면 빈 값). */
import { useMemo } from 'react';
import { useDesignTemplate } from '../../contexts/DesignTemplateContext';

const clean = (v) => (typeof v === 'string' ? v.trim() : '');

const parseHomeCopy = (designTemplate) => {
  let dt = designTemplate?.design_tokens;
  if (typeof dt === 'string') {
    try { dt = JSON.parse(dt); } catch (e) { return {}; }
  }
  const hc = dt?.home_copy;
  return (hc && typeof hc === 'object' && !Array.isArray(hc)) ? hc : {};
};

/* 구간 키(key) = 관리자 편집기(admin-dashboard-source HomeCopyEditor.jsx)의 키와 같다:
   intro · providers · new_games · recommended · quicklinks · sports · events · cta · sports_page · results_page */
export const useHomeCopy = () => {
  const designTemplate = useDesignTemplate();
  return useMemo(() => {
    const hc = parseHomeCopy(designTemplate);
    const part = (key) => ((hc[key] && typeof hc[key] === 'object') ? hc[key] : {});
    return {
      /* 구간 제목 — 비어 있으면 fallback(화면의 기본 제목) */
      title: (key, fallback) => clean(part(key).title) || fallback,
      /* 구간 설명 — 비어 있으면 '' (부르는 쪽이 줄을 그리지 않는다) */
      desc: (key) => clean(part(key).desc),
      /* 그 밖의 칸(line1·line2·button·link) 원값(공백만 자름) */
      field: (key, name) => clean(part(key)[name]),
    };
  }, [designTemplate]);
};

/* 소개 글 버튼 주소 — 사이트 안 경로('/'로 시작) 또는 http(s) 주소만 받는다. 그 밖(javascript: 등)은 null → 버튼을 그리지 않는다.
   관리자 입력을 그대로 링크에 넣지 않기 위한 한 곳의 판정이다(관리자 편집기 HomeCopyEditor.jsx 의 isUsableLink 와 같은 규칙).
   사이트 안 경로는 모양이 아니라 "브라우저가 실제로 해석한 출처가 같은가"로 판정한다.
   WHY(2026-09-30 독립 검토): '/' 로 시작해도 '//x'·'/\x' 는 브라우저가 바깥 주소로 읽는다(역슬래시 = 슬래시로 해석).
        공백·제어 문자는 주소 해석 때 지워져 모양이 바뀌므로(예: '/<탭>/x' → '//x') 아예 받지 않는다. */
const LINK_CHECK_ORIGIN = 'https://home-copy.invalid';   // 판정 전용 가상 출처(실제로 접속하지 않는다)
export const homeCopyLinkTarget = (raw) => {
  const v = clean(raw);
  if (!v || /[\s\u0000-\u001F\u007F]/.test(v)) return null;
  try {
    if (v.startsWith('/')) {
      return new URL(v, LINK_CHECK_ORIGIN).origin === LINK_CHECK_ORIGIN ? { internal: true, href: v } : null;
    }
    if (/^https?:\/\//i.test(v)) {
      const u = new URL(v);
      return (u.protocol === 'http:' || u.protocol === 'https:') ? { internal: false, href: v } : null;
    }
  } catch (e) {
    return null;   // 해석할 수 없는 주소
  }
  return null;
};
